// filename: src/components/UsageQuotaCard.tsx

import React from 'react';

function cx(...classes: Array<string | false | undefined | null>) {
    return classes.filter(Boolean).join(' ');
}

export function UsageQuotaCard({
    title,
    subtitle,
    used,
    limit,
    percentUsed,
    unitLabel,
    tone = 'zinc',
    isLoading,
    error,
}: {
    title: string;
    subtitle: string;
    used: number;
    limit: number;
    percentUsed: number;
    unitLabel: string; // "words" | "pages"
    tone?: 'zinc' | 'blue' | 'emerald' | 'red' | 'violet' | 'amber';
    isLoading?: boolean;
    error?: string | null;
}) {
    const pct = Number.isFinite(percentUsed)
        ? Math.max(0, Math.min(100, percentUsed))
        : 0;

    const tones: Record<
        string,
        { ring: string; bar: string; bg: string; text: string }
    > = {
        zinc: {
            ring: 'ring-zinc-200',
            bar: 'bg-zinc-900',
            bg: 'bg-white',
            text: 'text-zinc-900',
        },
        blue: {
            ring: 'ring-blue-200',
            bar: 'bg-blue-600',
            bg: 'bg-white',
            text: 'text-zinc-900',
        },
        emerald: {
            ring: 'ring-emerald-200',
            bar: 'bg-emerald-600',
            bg: 'bg-white',
            text: 'text-zinc-900',
        },
        red: {
            ring: 'ring-red-200',
            bar: 'bg-red-600',
            bg: 'bg-white',
            text: 'text-zinc-900',
        },
        violet: {
            ring: 'ring-violet-200',
            bar: 'bg-violet-600',
            bg: 'bg-white',
            text: 'text-zinc-900',
        },
        amber: {
            ring: 'ring-amber-200',
            bar: 'bg-amber-600',
            bg: 'bg-white',
            text: 'text-zinc-900',
        },
    };

    const t = tones[tone];

    const fmt = (n: number) => Intl.NumberFormat().format(Math.max(0, n || 0));

    return (
        <div className={cx('rounded-2xl p-4 shadow-sm ring-1', t.bg, t.ring)}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className={cx('text-sm font-semibold', t.text)}>
                        {title}
                    </div>
                    <div className="mt-1 text-xs text-zinc-500">{subtitle}</div>
                </div>

                <div className="shrink-0 text-right">
                    {isLoading ? (
                        <div className="text-xs text-zinc-500">Loading…</div>
                    ) : error ? (
                        <div className="text-xs text-red-600">Failed</div>
                    ) : (
                        <div className="text-xs font-semibold text-zinc-800">
                            {pct.toFixed(2)}%
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-3">
                {error ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
                        {error}
                    </div>
                ) : (
                    <>
                        <div className="flex items-baseline justify-between text-xs text-zinc-600">
                            <span>
                                {isLoading ? '—' : `${fmt(used)} ${unitLabel}`}{' '}
                                <span className="text-zinc-400">
                                    / {fmt(limit)}
                                </span>
                            </span>
                            <span className="text-zinc-500">
                                {isLoading
                                    ? '—'
                                    : `${fmt(limit - used)} remaining`}
                            </span>
                        </div>

                        <div className="mt-2 h-2 w-full rounded-full bg-zinc-100">
                            <div
                                className={cx(
                                    'h-2 rounded-full transition-[width] duration-300',
                                    t.bar,
                                )}
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
